import React, { useState } from 'react';
import { Battery, Brain, Heart, Activity } from 'lucide-react';
import { ReadinessBreakdown } from '../readiness/ReadinessBreakdown';
import { ReadinessInsights } from '../readiness/ReadinessInsights';
import { ReadinessTrends } from '../readiness/ReadinessTrends';
import { ReadinessKnowledge } from '../readiness/ReadinessKnowledge';
import { ReadinessMetricCard } from '../readiness/ReadinessMetricCard';
import { HealthMetrics } from '../../types/health';

interface ReadinessTabProps {
  metrics: HealthMetrics;
  onClose: () => void;
}

export function ReadinessTab({ metrics, onClose }: ReadinessTabProps) {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'24h' | '7d' | '30d'>('24h');

  return (
    <div className="space-y-6">
      {/* Readiness Metrics Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <ReadinessMetricCard
          title="Readiness Score"
          value={metrics.readiness}
          unit="%"
          icon={<Battery className="w-4 h-4" />}
          status={metrics.readiness > 70 ? 'success' : 'warning'}
          comparison="+5% vs yesterday"
        />
        <ReadinessMetricCard
          title="Recovery"
          value={metrics.recovery}
          unit="%"
          icon={<Heart className="w-4 h-4" />}
          status={metrics.recovery > 70 ? 'success' : 'warning'}
          comparison={`${metrics.recovery - 65}% vs baseline`}
        />
        <ReadinessMetricCard
          title="Sleep Score"
          value={metrics.sleep.score}
          unit="%"
          icon={<Brain className="w-4 h-4" />}
          status={metrics.sleep.score > 85 ? 'success' : 'warning'}
          comparison="+8% vs average"
        />
        <ReadinessMetricCard
          title="HRV"
          value={metrics.hrv.current}
          unit="ms"
          icon={<Activity className="w-4 h-4" />}
          status={metrics.hrv.current > metrics.hrv.yesterday ? 'success' : 'warning'}
          comparison={`${metrics.hrv.current - metrics.hrv.yesterday}ms vs yesterday`}
        />
      </div>

      {/* Readiness Score Trends */}
      <ReadinessTrends 
        metrics={metrics}
        timeframe={selectedTimeframe}
        onTimeframeChange={setSelectedTimeframe}
      />

      {/* Readiness Breakdown */}
      <ReadinessBreakdown metrics={metrics} />

      {/* ML-Powered Insights */}
      <ReadinessInsights metrics={metrics} />

      {/* Knowledge Center */}
      <ReadinessKnowledge />
    </div>
  );
}